import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FormError, SubmitButton } from "@/components/common";
import { LineRow, toBody } from "@/modules/quotes/QuoteEditor";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtCents } from "@/lib/format";
import { t } from "@/i18n";

const METHODS = ["card", "cash", "bank_transfer", "other"];

function useSubmit(fn, onDone, msg) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await fn();
      toast.success(msg);
      onDone(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

export function PaymentDialog({ inv, mode = "payment", onClose, onDone }) {
  const max = mode === "payment" ? inv.balance_cents : inv.paid_cents;
  const [f, setF] = useState({ method: "card", amount: ((mode === "payment" ? max : inv.balance_cents < 0 ? -inv.balance_cents : max) / 100).toFixed(2), date: "", reference: "" });
  const { busy, error, submit } = useSubmit(() => api.post(`/invoices/${inv.id}/${mode === "payment" ? "payments" : "refunds"}`, { ...f, date: f.date || null }),
    onDone, mode === "payment" ? t("invoices.paymentRecorded") : t("invoices.refundRecorded"));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${mode}-dialog`}>
        <DialogHeader><DialogTitle>{mode === "payment" ? t("invoices.recordPayment") : t("invoices.recordRefund")}</DialogTitle>
          <DialogDescription>{mode === "payment" ? t("invoices.paymentHint", { max: fmtCents(max) }) : t("invoices.refundHint", { max: fmtCents(max) })}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("invoices.method")}>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid={`${mode}-method`}><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{t(`invoices.methods.${m}`)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("invoices.amount")}><Input type="number" step="0.01" min="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid={`${mode}-amount`} /></Field>
            <Field label={t("invoices.date")} hint={t("invoices.dateHint")}><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid={`${mode}-date`} /></Field>
            <Field label={t("invoices.reference")}><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid={`${mode}-reference`} /></Field>
          </div>
          <p className="text-xs text-slate-500">{t("invoices.noCharge")}</p>
          <SubmitButton busy={busy} className="w-full" data-testid={`${mode}-submit`}>{mode === "payment" ? t("invoices.recordPaymentReceived") : t("invoices.recordRefund")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CreditNoteDialog({ inv, onClose, onDone }) {
  const [full, setFull] = useState(!inv.credited_cents);
  const [reason, setReason] = useState("");
  const [restock, setRestock] = useState(false);
  const [lines, setLines] = useState([{ type: "labour", description: "", qty: "1", unit_price: "", discount_pct: "0", cost_price: "" }]);
  const { busy, error, submit } = useSubmit(() => api.post(`/invoices/${inv.id}/credit-notes`, { full, reason, return_to_stock: restock, lines: full ? [] : toBody({ lines, discount_type: "none", discount_value: 0 }).lines }),
    onDone, t("invoices.creditIssued"));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="credit-note-dialog">
        <DialogHeader><DialogTitle>{t("invoices.creditNote")}</DialogTitle><DialogDescription>{t("invoices.creditHint", { number: inv.number })}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          <label className="flex items-center gap-3 text-sm"><Switch checked={full} disabled={Boolean(inv.credited_cents)} onCheckedChange={setFull} data-testid="credit-full-switch" /> {t("invoices.creditFull", { total: fmtCents(inv.totals.total_cents) })}</label>
          {!full && <div className="space-y-2">
            {lines.map((l, i) => <LineRow key={i} l={l} i={i} set={(j, p) => setLines(lines.map((x, k) => (k === j ? { ...x, ...p } : x)))} remove={(j) => setLines(lines.filter((_, k) => k !== j))} />)}
            <button type="button" className="text-sm font-semibold text-teal-700" onClick={() => setLines([...lines, { type: "part", description: "", qty: "1", unit_price: "", discount_pct: "0", cost_price: "" }])} data-testid="credit-add-line">+ {t("invoices.addLine")}</button>
          </div>}
          {inv.lines?.some((l) => l.part_id) && <label className="flex items-center gap-3 text-sm"><Switch checked={restock} onCheckedChange={setRestock} data-testid="credit-restock-switch" /> {t("b8.inv.restock")}</label>}
          <Field label={t("invoices.creditReason")}><Textarea required minLength={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="credit-reason" /></Field>
          <SubmitButton busy={busy} className="w-full" data-testid="credit-submit">{t("invoices.issueCredit")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function VoidDialog({ inv, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const { busy, error, submit } = useSubmit(() => api.post(`/invoices/${inv.id}/void`, { reason }), onDone, t("invoices.voided"));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="void-dialog">
        <DialogHeader><DialogTitle>{t("invoices.voidTitle")}</DialogTitle><DialogDescription>{t("invoices.voidText")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          <Field label={t("invoices.voidReason")}><Textarea required minLength={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="void-reason" /></Field>
          <SubmitButton busy={busy} variant="destructive" className="w-full" data-testid="void-submit">{t("invoices.void")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function NewInvoiceDialog({ onClose, onDone }) {
  const [f, setF] = useState({ customer_id: "", vehicle_id: "" });
  const customers = useFetch("/customers");
  const vehicles = useFetch("/vehicles", { customer_id: f.customer_id || "none" });
  const { busy, error, submit } = useSubmit(() => api.post("/invoices", { customer_id: f.customer_id, vehicle_id: f.vehicle_id || null }), onDone, t("invoices.created"));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-invoice-dialog">
        <DialogHeader><DialogTitle>{t("invoices.new")}</DialogTitle><DialogDescription>{t("invoices.newHint")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          <CustomerVehicle f={f} setF={setF} customers={customers.data} vehicles={vehicles.data} prefix="inv" />
          <SubmitButton busy={busy} disabled={!f.customer_id} className="w-full" data-testid="inv-create-submit">{t("invoices.createDraft")}</SubmitButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CustomerVehicle({ f, setF, customers, vehicles, prefix }) {
  return (
    <>
      <Field label={t("sched.customer")}>
        <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v, vehicle_id: "" })}>
          <SelectTrigger data-testid={`${prefix}-customer-select`}><SelectValue placeholder={t("sched.chooseCustomer")} /></SelectTrigger>
          <SelectContent>{(customers || []).map((c) => <SelectItem key={c.id} value={c.id}>{`${c.first_name} ${c.last_name}`.trim()}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      {f.customer_id && <Field label={t("sched.vehicle")}>
        <Select value={f.vehicle_id} onValueChange={(v) => setF({ ...f, vehicle_id: v })}>
          <SelectTrigger data-testid={`${prefix}-vehicle-select`}><SelectValue placeholder={t("sched.chooseVehicle")} /></SelectTrigger>
          <SelectContent>{(vehicles || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.make} {v.model}</SelectItem>)}</SelectContent>
        </Select>
      </Field>}
    </>
  );
}
